import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api, { type User, type AuthResult } from '../lib/api'

export interface RewardEligibility {
  eligible: boolean
  broadcaster_type: string
  message?: string
}

export const useAuthStore = defineStore('auth', () => {
  // State
  const accessToken = ref<string | null>(localStorage.getItem('access_token'))
  const refreshToken = ref<string | null>(localStorage.getItem('refresh_token'))
  const user = ref<User | null>(JSON.parse(localStorage.getItem('user') || 'null'))
  
  // Reward eligibility state (session only, not persisted)
  const rewardEligibility = ref<RewardEligibility | null>(null)
  const eligibilityLoading = ref(false)
  const eligibilityChecked = ref(false)

  // Getters
  const isAuthenticated = computed(() => !!accessToken.value && !!user.value)
  const canCreateRewards = computed(() => rewardEligibility.value?.eligible ?? false)
  const isEligibilityLoading = computed(() => eligibilityLoading.value)
  const hasCheckedEligibility = computed(() => eligibilityChecked.value)

  // Actions
  async function refreshTokens() {
    if (!refreshToken.value) {
      throw new Error('No refresh token available')
    }

    const { data } = await api.post<AuthResult>('/auth/refresh', {
      refresh_token: refreshToken.value,
    })

    setTokens(data.access_token, data.refresh_token)
    setUser(data.user)

    return data
  }

  function setTokens(access: string, refresh: string) {
    accessToken.value = access
    refreshToken.value = refresh
    localStorage.setItem('access_token', access)
    localStorage.setItem('refresh_token', refresh)
  }

  function setUser(userData: User) {
    user.value = userData
    localStorage.setItem('user', JSON.stringify(userData))
  }

  function logout() {
    accessToken.value = null
    refreshToken.value = null
    user.value = null
    rewardEligibility.value = null
    eligibilityChecked.value = false
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
    localStorage.removeItem('user')
  }

  function login() {
    // Redirect to backend which handles the OAuth flow
    window.location.href = '/api/v1/auth/twitch/login'
  }

  async function checkRewardEligibility() {
    // Don't check if not authenticated or already checked
    if (!isAuthenticated.value || eligibilityChecked.value) {
      return rewardEligibility.value
    }

    eligibilityLoading.value = true
    
    try {
      const { data } = await api.get<RewardEligibility>('/config/me/rewards/eligibility')
      rewardEligibility.value = data
      eligibilityChecked.value = true
      return data
    } catch (e) {
      console.error('Failed to check reward eligibility:', e)
      // Assume eligible if check fails - the actual create call will fail anyway
      rewardEligibility.value = { eligible: true, broadcaster_type: '' }
      eligibilityChecked.value = true
      return rewardEligibility.value
    } finally {
      eligibilityLoading.value = false
    }
  }

  // Wait for eligibility check to complete
  async function waitForEligibilityCheck() {
    if (eligibilityChecked.value) return rewardEligibility.value
    if (!isAuthenticated.value) return null
    return await checkRewardEligibility()
  }

  return {
    // State
    accessToken,
    refreshToken,
    user,
    rewardEligibility,
    // Getters
    isAuthenticated,
    canCreateRewards,
    isEligibilityLoading,
    hasCheckedEligibility,
    // Actions
    login,
    refreshTokens,
    setTokens,
    setUser,
    logout,
    checkRewardEligibility,
    waitForEligibilityCheck,
  }
})
